/**
 * Helpers for biasing Mapbox address geocoding to the org's service region
 * (RUT-3034). A street typed without a city (e.g. "18313 Nicklaus Way") ranks
 * nationally and can return out-of-state matches; passing Mapbox a `proximity`
 * center makes the local address rank first.
 *
 * The center is optional on purpose: when the org's region isn't known we must
 * omit the param entirely rather than send `proximity=undefined`.
 */

export interface ProximityCenter {
  lng: number;
  lat: number;
}

/**
 * The Mapbox `proximity` param value ("lng,lat") for a center, or `undefined`
 * when there is no usable center — so callers can leave the param off.
 */
export function proximityParam(center?: ProximityCenter | null): string | undefined {
  if (!center) return undefined;
  const { lng, lat } = center;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return undefined;
  return `${lng},${lat}`;
}

/**
 * Set `proximity` on the given search params only when a valid center is known.
 * A no-op otherwise, preserving the un-biased (national) request.
 */
export function applyProximity(params: URLSearchParams, center?: ProximityCenter | null): void {
  const value = proximityParam(center);
  if (value) params.set('proximity', value);
}
